import styled from "@emotion/styled";
import React, { useEffect, useId, useState } from 'react';
import { Grid, InputLabel } from 'src/components';
import { colors as themeColors } from 'src/theme';

const Option = styled.label({
  cursor: 'pointer',
  flex: 1,
  zIndex: 1,

  'input': {
    position: 'absolute',
    opacity: 0,
  },
});

const OptionText = styled.span((props: {
  colors: { background?: string, text?: string, backgroundSelected?: string, textSelected?: string },}) => ({
  color: props.colors.text,
  backgroundColor: props.colors.background,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  borderRadius: '8px',
  fontSize: '16px',
  lineHeight: '18px',
  fontWeight: 400,
  textAlign: 'center' as const,
  pointerEvents: 'none' as const,

  'input:checked + &': {
    color: props.colors.textSelected,
    backgroundColor: props.colors.backgroundSelected,
    fontWeight: 700,
  },

  'input:focus-visible + &': {
    outline: `2px solid ${themeColors.primary1}`,
    outlineOffset: '2px',
  },
}));

const ValidationError = styled.div({
  color: themeColors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

const GridStyle = styled(Grid)({});

interface RadioOption {
  value: string;
  label: string;
}

interface RadioButtonGroupInputProps {
  options: RadioOption[];
  value?: string;
  onChange?: (selectedOption: string) => void;
  className?: string;
  label?: string;
  error?: string;
  colors?: {
    background?: string;
    text?: string;
    backgroundSelected?: string;
    textSelected?: string;
  },
}

export const RadioButtonGroupInput: React.FC<RadioButtonGroupInputProps> = ({
  options, value, onChange, className, label, error, colors: customColors = {}
}) => {
  const colors = {
    background: themeColors.background6,
    text: themeColors.text2,
    backgroundSelected: themeColors.primary1,
    textSelected: themeColors.text7,
    ...customColors
  };

  const groupId = useId();
  const [selectedOption, setSelectedOption] = useState<string | undefined>(value);

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  // Funkcija za ažuriranje stanja
  const handleOptionChange = (optionValue: string) => {
    setSelectedOption(optionValue);
    if (onChange) onChange(optionValue); // Pozivamo onChange prop funkciju sa izabranom opcijom
  };

  const columns = `repeat(${options.length}, 1fr)`;

  return (
    <div>
      {label ? <InputLabel>{label}</InputLabel> : null}
      <GridStyle columns={columns} gap='4px' className={className}>
        {options.map((option) => (
          <Option
            key={option.value}
            htmlFor={`${groupId}-${option.value}`}
          >
            <input
              id={`${groupId}-${option.value}`}
              type="radio"
              name={groupId}
              value={option.value}
              checked={selectedOption === option.value}
              onChange={() => handleOptionChange(option.value)}
            />
            <OptionText colors={colors}>{option.label}</OptionText>
          </Option>
        ))}
      </GridStyle>
      {error ? <ValidationError>{error}</ValidationError> : null}
    </div>
  );
};